import * as React from 'react'
import { ReactElement, useContext, useMemo, useRef, useState } from 'react'

import OverflowToolbarPanel, { HammockToolbarButtonData, HammockToolbarProps } from '../../../../components/Overflow/OverflowToolbarPanel'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { FileRouteMatchProps, usePageStyles, ViewFileProps } from '../ViewFile'
import DocumentFlatTree, { FolderDriveItem } from '../../../../components/Document/DocumentFlatTree'
import DocumentFlatList, { DocumentFlatListCache } from '../../../../components/Document/DocumentFlatList'
import {
    Badge,
    Button,
    Card,
    makeStyles,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    Popover,
    PopoverSurface,
    tokens,
} from '@fluentui/react-components'
import {
    AddSquareMultipleFilled,
    AddSquareMultipleRegular,
    ArrowDownloadFilled,
    ArrowDownloadRegular,
    ArrowSortFilled,
    ArrowSortRegular,
    ArrowUploadFilled,
    ArrowUploadRegular,
    bundleIcon,
    ClipboardTaskAddFilled,
    ClipboardTaskAddRegular,
    CopyArrowRightFilled,
    CopyArrowRightRegular,
    CopySelectFilled,
    CopySelectRegular,
    DeleteFilled,
    DeleteRegular,
    DismissRegular,
    DocumentAddFilled,
    DocumentAddRegular,
    DocumentPdfFilled,
    DocumentPdfRegular,
    FolderAddFilled,
    FolderAddRegular,
    FolderZipFilled,
    FolderZipRegular,
    ListBarFilled,
    ListBarRegular,
    ListBarTreeOffsetFilled,
    ListBarTreeOffsetRegular,
    MailAddFilled,
    MailAddRegular,
    MailAttachFilled,
    MailAttachRegular,
    QuestionCircleFilled,
    QuestionCircleRegular,
    RenameFilled,
    RenameRegular,
} from '@fluentui/react-icons'
import SimpleDocumentFlatTreeItem from '../../../../components/Document/SimpleDocumentFlatTreeItem'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import HammockDialog, { useDialogStyles } from '../../../components/Dialog/HammockDialog'
import { useTranslation } from 'react-i18next'
import CreateFolderDialogBody, {
    CreateFolderDialogBodyProps,
    CreateFolderDialogBodyValues,
    getCreateRootFolderDialogBodySchema,
    getCreateSubFolderDialogBodySchema,
} from '../../../components/Dialog/DriveItem/CreateFolderDialogBody'
import HammockFormDialog from '../../../components/Dialog/HammockFormDialog'
import OfficeDocumentDialogBody, {
    getOfficeDocumentDialogBodySchema,
    OfficeDocumentDialogBodyProps,
    OfficeDocumentDialogBodyValues,
} from '../../../components/Dialog/DriveItem/OfficeDocumentDialogBody'
import { DocumentContext, DocumentSortOrder } from '../../../../config/documentConfig'
import PreviewDocumentDialogBody, { PreviewDocumentDialogBodyProps } from '../../../components/Dialog/DriveItem/PreviewDocumentDialogBody'
import MutateDocumentDialogBody, {
    getMutateDocumentDialogBodySchema,
    MutateDocumentAction,
    MutateDocumentDialogBodyProps,
    MutateDocumentDialogBodyValues,
} from '../../../components/Dialog/DriveItem/MutateDocumentDialogBody'
import DeleteDocumentDialogBody, { DeleteDocumentDialogBodyProps } from '../../../components/Dialog/DriveItem/DeleteDocumentDialogBody'
import { getDownloadUrl, getDriveItemDownloadUrl, isConvertableToPdf, moveDriveItems } from '../../../../helpers/documentHelper'
import useSharePoint from '../../../../hooks/useSharePoint'
import { buildZipParams, validateZipSelection } from '../../../../helpers/zipHelper'
import MessageContext, { MAX_MESSAGE_SIZE } from '../../../../context/MessageContext'
import useSubmitDocumentGenerationRequest from '../../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import {
    DocumentGenerationContextData,
    DocumentGenerationStatusData,
    DocumentGenerationType,
} from '../../../../hooks/document/useDocumentGenerationHandler'
import { QueueData, QueueStatus } from '../../../../hooks/useQueueStateHandler'
import { addFileAttachmentAsync, isOfficeInitialized } from '../../../../helpers/outlookHelper'
import { generatePath } from 'react-router'
import { fileRoutePrefix } from '../../../../hooks/file/useFilePages'
import { ModuleType } from '../../../../context/GlobalContext'
import useModules from '../../../../hooks/module/useModules'
import { useSettings } from '../../../../hooks/useSettings'
import { useHistory, useRouteMatch } from 'react-router-dom'
import ComposeEmailDialogBody, { ComposeEmailDialogBodyProps } from '../../../components/Dialog/OutlookItem/ComposeEmailDialogBody'
import { isAppContext } from '../../../../helpers/appHelper'
import { AppContext } from '../../../../config/appConfig'
import useAxios from '../../../../hooks/useAxios'
import Dropzone from '../../../../components/Dropzone/Dropzone'
import ImportDocumentDialogBody, {
    getImportDocumentDialogBodySchema,
    ImportDocumentDialogBodyProps,
    ImportDocumentDialogBodyValues,
} from '../../../components/Dialog/DriveItem/ImportDocumentDialogBody'
import { DropzoneRef } from 'react-dropzone'
import useLocalStorage from '../../../../hooks/useLocalStorage'
import AuthorizationContext from '../../../../context/AuthorizationContext'
import { useFileFolderTree } from '../../../../context/FileFolderTreeContext'
import Loading from '../../../../components/Loading'
import { useApplicationStyles } from '../../../../pages/App'

const AddIcon = bundleIcon(AddSquareMultipleFilled, AddSquareMultipleRegular)
const AttachIcon = bundleIcon(MailAttachFilled, MailAttachRegular)
const DocumentAddIcon = bundleIcon(DocumentAddFilled, DocumentAddRegular)
const TaskAddIcon = bundleIcon(ClipboardTaskAddFilled, ClipboardTaskAddRegular)
const MailAddIcon = bundleIcon(MailAddFilled, MailAddRegular)
const DocumentPdfIcon = bundleIcon(DocumentPdfFilled, DocumentPdfRegular)
const FolderAddIcon = bundleIcon(FolderAddFilled, FolderAddRegular)
const FolderZipIcon = bundleIcon(FolderZipFilled, FolderZipRegular)
const RenameIcon = bundleIcon(RenameFilled, RenameRegular)
const CopyIcon = bundleIcon(CopySelectFilled, CopySelectRegular)
const MoveIcon = bundleIcon(CopyArrowRightFilled, CopyArrowRightRegular)
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular)
const ImportIcon = bundleIcon(ArrowUploadFilled, ArrowUploadRegular)
const SortIcon = bundleIcon(ArrowSortFilled, ArrowSortRegular)
const DownloadIcon = bundleIcon(ArrowDownloadFilled, ArrowDownloadRegular)
const HelpIcon = bundleIcon(QuestionCircleFilled, QuestionCircleRegular)

const useOverviewStyles = makeStyles({
    // The selection pill sits next to the Overflow toolbar instead of inside it (same pattern
    // as the edit button in ViewFileHeader), so it stays visible on Narrow/Tiny where toolbar
    // items collapse into the overflow menu.
    toolbarRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalS,
        alignItems: 'center',
        minHeight: '32px',
    },
    selectionPill: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
        flexShrink: 0,
    },
    helpPopover: {
        maxWidth: '320px',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        fontSize: tokens.fontSizeBase200,
    },
    helpList: {
        margin: 0,
        paddingLeft: tokens.spacingHorizontalL,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
})

// Graph download URLs carry a content-disposition attachment header, so navigating an
// anchor to them downloads without leaving the page.
const triggerBrowserDownload = (url: string): void => {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.rel = 'noopener'
    anchor.click()
}

export enum ViewType {
    TreeView = 'TREE_VIEW',
    ListView = 'LIST_VIEW',
}

type FileDocumentOverviewProps = ViewFileProps & {
    selectedDriveItems: FolderDriveItem[]
    setSelectedDriveItems: (items: FolderDriveItem[]) => void
}

export default function FileDocumentOverview(props: FileDocumentOverviewProps): ReactElement {
    const appStyles = useApplicationStyles()
    const overviewStyles = useOverviewStyles()
    const axios = useAxios()
    const history = useHistory()
    const { host } = useContext(AuthorizationContext)
    const { maxMessageSize } = useContext(MessageContext)
    const { invalidate: invalidateFolderTree } = useFileFolderTree()
    const modules = useModules()
    const pageStyles = usePageStyles()
    const dialogStyles = useDialogStyles()
    const { sharePoint } = useSharePoint()
    const { t: translate } = useTranslation()
    const { isViewport, viewport } = useViewport()

    const viewportNarrow = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])
    const smallMenu = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])
    const routeMatch = useRouteMatch<FileRouteMatchProps>()

    const toolbarProps = useMemo<HammockToolbarProps>(
        (): HammockToolbarProps => ({
            size: 'small',
            className: pageStyles.toolbar,
        }),
        [smallMenu],
    )

    // Default sort order comes from the environment-wide DOCUMENTS_SORT setting; a stored
    // user choice in localStorage always wins over that default (HAM-4057). An absent or
    // invalid setting value falls back to ModifiedDesc.
    const { loaded: settingsLoaded, settings } = useSettings()
    const settingSortOrder = useMemo<DocumentSortOrder>(() => {
        const value = settings?.documentsSort as DocumentSortOrder
        if (value && !Object.values(DocumentSortOrder).includes(value)) {
            console.warn(`[FileDocumentOverview] ignoring invalid DOCUMENTS_SORT setting value "${settings?.documentsSort}"`)
            return DocumentSortOrder.ModifiedDesc
        }
        return value ?? DocumentSortOrder.ModifiedDesc
    }, [settings?.documentsSort])
    const {
        loaded: loadedSortOrder,
        values: sortOrder,
        setValues: setSortOrder,
    } = useLocalStorage<DocumentSortOrder>({
        key: 'HammockFileDocumentOverviewSorting',
        hostKey: host?.RowKey,
        defaultValue: settingSortOrder,
        fromJson: values => values,
        toJson: values => values,
        load: host !== undefined && settingsLoaded,
    })
    // Deliberately NOT persisted (HAM-4057): every dossier opens in the tree view.
    const [viewType, setViewType] = useState<ViewType>(ViewType.TreeView)

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [openCreateStandardDocument, setOpenCreateStandardDocument] = useState(false)
    const [openCreateRootFolder, setOpenCreateRootFolder] = useState(false)
    const [openCreateSubFolder, setOpenCreateSubFolder] = useState(false)
    const [openMutateDocuments, setOpenMutateDocuments] = useState<Pick<MutateDocumentDialogBodyProps, 'action' | 'items'>>()
    // folderId presets the import dialog's target folder when files were dropped on a folder row.
    const [openImportDocuments, setOpenImportDocuments] = useState<Pick<ImportDocumentDialogBodyProps, 'items'> & { folderId?: string }>()
    const [openDeleteDocuments, setOpenDeleteDocuments] = useState(false)
    const [openComposeEmail, setOpenComposeEmail] = useState(false)
    const [openPreviewDocument, setOpenPreviewDocument] = useState<FolderDriveItem>()

    const dropzoneRef = useRef<DropzoneRef>()

    const [allDriveItems, setAllDriveItems] = useState<FolderDriveItem[]>(undefined)
    const [filteredDriveItems, setFilteredDriveItems] = useState<FolderDriveItem[]>(undefined)
    const documentDriveItems = useMemo(
        () => (filteredDriveItems ? filteredDriveItems : allDriveItems)?.filter(item => item.itemType === 'leaf'),
        [allDriveItems, filteredDriveItems],
    )

    const updateDriveItemsFunc = useRef<(addDriveItems: DriveItem[], removeDriveItems: DriveItem[]) => void>(() => {})
    const reloadDriveItemsFunc = useRef<() => void>(() => {})
    // Crawl cache for the flat list view so switching tree <-> list within one dossier does
    // not re-crawl. Dropped on every reload (mutations reload in both views), which makes a
    // later switch to the list view fetch live data again (HAM-4057).
    const flatListCacheRef = useRef<DocumentFlatListCache>(undefined)
    const registerReloadDriveItems = React.useCallback((setter: () => void) => {
        reloadDriveItemsFunc.current = () => {
            flatListCacheRef.current = undefined
            setter()
        }
    }, [])

    const [errors, setErrors] = useState<Error[]>([])

    const submitPdfRequest = useSubmitDocumentGenerationRequest(
        DocumentGenerationType.PdfConversion,
        {
            [QueueStatus.Finished]: () => {
                reloadDriveItemsFunc.current?.()
            },
        },
        true,
    )

    const submitZipRequest = useSubmitDocumentGenerationRequest(
        DocumentGenerationType.Zip,
        {
            [QueueStatus.Finished]: () => {
                reloadDriveItemsFunc.current?.()
            },
        },
        true,
    )

    // The help popover is controlled and anchored to the toolbar row instead of wrapping the
    // help button: the button may sit inside the overflow menu (Tiny), where a PopoverTrigger
    // would unmount when the menu closes. Clicking outside closes it via onOpenChange.
    const [helpOpen, setHelpOpen] = useState(false)
    const toolbarRowRef = useRef<HTMLDivElement>(null)

    const clearSelection = React.useCallback(() => {
        props.setSelectedDriveItems([])
        props.setSelectedDriveItemIds([])
    }, [props.setSelectedDriveItems, props.setSelectedDriveItemIds])

    // Drag & drop move: conflictBehavior 'fail' so dropping onto a folder that already has an
    // item with the same name surfaces an error instead of silently renaming (the Move dialog
    // keeps 'rename' — there the user typed a name deliberately).
    const handleMoveItems = React.useCallback(
        (_ids: string[], items: FolderDriveItem[], targetFolder: FolderDriveItem) => {
            moveDriveItems(axios, sharePoint?.driveId, items, targetFolder.id, 'fail')
                .then(() => {
                    reloadDriveItemsFunc.current?.()
                    clearSelection()
                    // A move may have relocated a folder — drop the party picker's cached copy.
                    invalidateFolderTree()
                })
                .catch(error => {
                    const status = error?.response?.status
                    const code = error?.response?.data?.error?.code
                    const messageKey =
                        status === 409 || code === 'nameAlreadyExists'
                            ? 'file.pivot.document.drag.move.conflict'
                            : 'file.pivot.document.drag.move.error'
                    setErrors(prevErrors => [...prevErrors, new Error(translate(messageKey))])
                    // Part of a multi-item move may have succeeded before the failure — reload
                    // so the tree reflects the real state.
                    reloadDriveItemsFunc.current?.()
                })
        },
        [axios, sharePoint?.driveId, clearSelection, invalidateFolderTree, translate],
    )

    const switchView = React.useCallback(
        (next: ViewType) => {
            if (next === viewType) {
                return
            }
            // A tree selection may contain folders, which the flat list cannot represent.
            clearSelection()
            setViewType(next)
        },
        [viewType, clearSelection],
    )

    const buttons: HammockToolbarButtonData[] = useMemo<HammockToolbarButtonData[]>((): HammockToolbarButtonData[] => {
        const selectedCount = props.selectedDriveItems.length
        const singleSelectedFile = selectedCount === 1 && props.selectedDriveItems[0]?.itemType === 'leaf' ? props.selectedDriveItems[0] : undefined
        return [
            {
                value: 'email-add-document',
                icon: <AttachIcon />,
                titleKey: 'file.pivot.document.email.add',
                filter: () => isOfficeInitialized() && isAppContext([AppContext.Compose]),
                disabled: props.selectedDriveItems.filter(item => item.itemType === 'leaf').length === 0,
                disabledReasonKey: 'file.pivot.document.disabled.select_documents',
                onClick: () => {
                    Promise.all(
                        props.selectedDriveItems
                            .filter(item => item.itemType === 'leaf')
                            .map(item =>
                                getDriveItemDownloadUrl(item.id, axios).then(downloadUrl => {
                                    addFileAttachmentAsync(downloadUrl, item.name, undefined, (_, error) => {
                                        if (error) {
                                            setErrors([...errors, new Error(`${item.name}: ${error.message}`)])
                                        }
                                    })
                                }),
                            ),
                    ).then(() => {
                        props.setSelectedDriveItems([])
                        props.setSelectedDriveItemIds([])
                    })
                },
            },
            {
                value: 'email-add-document-pdf',
                icon: <DocumentPdfIcon />,
                titleKey: 'file.pivot.document.email.add.pdf',
                filter: () => isOfficeInitialized() && isAppContext([AppContext.Compose]),
                disabled: props.selectedDriveItems.filter(item => item.itemType === 'leaf').length === 0,
                disabledReasonKey: 'file.pivot.document.disabled.select_documents',
                onClick: () => {
                    props.selectedDriveItems
                        .filter(item => item.itemType === 'leaf')
                        .forEach(async item => {
                            if (isConvertableToPdf(item.name)) {
                                const payload = {
                                    itemId: item.id,
                                    fileName: item.name,
                                    folderId: item.parent.id,
                                }
                                submitPdfRequest(payload, item.name.replace(/\.[a-zA-Z]{3,}$/, '.pdf'), {
                                    [QueueStatus.Finished]: (e: QueueData<any, DocumentGenerationStatusData, DocumentGenerationContextData>) => {
                                        setTimeout(() => {
                                            addFileAttachmentAsync(
                                                getDownloadUrl(e.status.driveItem),
                                                e.status.driveItem.name,
                                                undefined,
                                                (_, error) => {
                                                    if (error) {
                                                        setErrors([...errors, new Error(`${item.name}: ${error.message}`)])
                                                    }
                                                },
                                            )
                                        }, Math.random() * 2000)
                                    },
                                })
                            } else {
                                getDriveItemDownloadUrl(item.id, axios).then(downloadUrl => {
                                    setTimeout(
                                        () =>
                                            addFileAttachmentAsync(downloadUrl, item.name, undefined, (_, error) => {
                                                if (error) {
                                                    setErrors([...errors, new Error(`${item.name}: ${error.message}`)])
                                                }
                                            }),
                                        Math.random() * 2000,
                                    )
                                })
                            }
                        })
                    props.setSelectedDriveItems([])
                    props.setSelectedDriveItemIds([])
                },
            },
            {
                value: 'add',
                icon: <AddIcon />,
                titleKey: 'file.pivot.document.create.label',
                filter: () => !isAppContext([AppContext.Compose]),
                childButtons: [
                    {
                        value: 'add-document',
                        icon: <DocumentAddIcon />,
                        titleKey: 'file.pivot.document.create.standard',
                        onClick: () => setOpenCreateStandardDocument(true),
                    },
                    {
                        value: 'add-task',
                        icon: <TaskAddIcon />,
                        titleKey: 'file.pivot.document.create.task',
                        onClick: () =>
                            history.push(
                                generatePath(`${fileRoutePrefix}/${modules.has(ModuleType.Workflow) ? 'workflow' : 'task'}/add`, routeMatch.params),
                                {
                                    attachmentIds: props.selectedDriveItems?.map(item => item.id) ?? [],
                                },
                            ),
                        disabled: props.selectedDriveItems.length === 0,
                    },
                    {
                        value: 'add-email',
                        icon: <MailAddIcon />,
                        titleKey: 'file.pivot.document.create.email',
                        onClick: () => setOpenComposeEmail(true),
                        filter: () => isOfficeInitialized(),
                        disabled: props.selectedDriveItems.length === 0,
                    },
                    {
                        value: 'add-root-folder',
                        icon: <FolderAddIcon />,
                        titleKey: 'file.pivot.document.create.folder.root',
                        filter: () => viewType === ViewType.TreeView,
                        onClick: () => setOpenCreateRootFolder(true),
                    },
                    {
                        value: 'add-sub-folder',
                        icon: <FolderAddIcon />,
                        titleKey: 'file.pivot.document.create.folder.sub',
                        filter: () => viewType === ViewType.TreeView,
                        onClick: () => setOpenCreateSubFolder(true),
                    },
                ],
            },
            {
                value: 'divider1',
                divider: true,
                filter: () => !isAppContext([AppContext.Compose]),
            },
            {
                value: 'email-add-document-zip',
                icon: <FolderZipIcon />,
                titleKey: 'file.pivot.document.email.add.zip',
                disabled: props.selectedDriveItems.length === 0,
                disabledReasonKey: 'file.pivot.document.disabled.select_items',
                onClick: () => {
                    const selectedDriveItems = props.selectedDriveItems
                    const validationError =
                        isOfficeInitialized() && isAppContext([AppContext.Compose])
                            ? validateZipSelection(selectedDriveItems, translate, maxMessageSize?.data ?? MAX_MESSAGE_SIZE)
                            : undefined
                    if (validationError) {
                        setErrors([...errors, validationError])
                        return
                    }
                    const payload = buildZipParams(selectedDriveItems, props.file.documentRootId, props.file.reference)
                    submitZipRequest(payload, payload.fileName, {
                        [QueueStatus.Finished]: (e: QueueData<any, DocumentGenerationStatusData, DocumentGenerationContextData>) => {
                            if (isOfficeInitialized() && isAppContext([AppContext.Compose])) {
                                addFileAttachmentAsync(getDownloadUrl(e.status.driveItem), e.status.driveItem.name, undefined, (_, error) => {
                                    if (error) {
                                        setErrors([...errors, new Error(`${e.status.driveItem.name}: ${error.message}`)])
                                    }
                                })
                            }
                        },
                    })
                    props.setSelectedDriveItems([])
                    props.setSelectedDriveItemIds([])
                },
            },
            {
                value: 'convert-pdf',
                icon: <DocumentPdfIcon />,
                titleKey: 'file.pivot.document.convert.pdf',
                filter: () => !isAppContext([AppContext.Compose]),
                disabledReasonKey: 'file.pivot.document.disabled.select_convertible',
                disabled:
                    props.selectedDriveItems.length === 0 ||
                    props.selectedDriveItems.filter(item => item.itemType === 'branch').length > 0 ||
                    props.selectedDriveItems.some(item => !isConvertableToPdf(item.name)),
                onClick: () => {
                    props.selectedDriveItems.forEach(item => {
                        if (isConvertableToPdf(item.name)) {
                            const payload = {
                                itemId: item.id,
                                fileName: item.name,
                                folderId: item.parent.id,
                            }
                            submitPdfRequest(payload, item.name.replace(/\.[a-zA-Z]{3,}$/, '.pdf'))
                        }
                    })
                },
            },
            {
                value: 'rename',
                icon: <RenameIcon />,
                titleKey: 'file.pivot.document.mutate.rename.button',
                disabled: props.selectedDriveItems.length !== 1,
                disabledReasonKey: 'file.pivot.document.disabled.select_one',
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => setOpenMutateDocuments({ action: MutateDocumentAction.Rename, items: props.selectedDriveItems }),
            },
            {
                value: 'copy',
                icon: <CopyIcon />,
                titleKey: 'file.pivot.document.mutate.copy.button',
                disabled: props.selectedDriveItems.length === 0,
                disabledReasonKey: 'file.pivot.document.disabled.select_items',
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => setOpenMutateDocuments({ action: MutateDocumentAction.Copy, items: props.selectedDriveItems }),
            },
            {
                value: 'move',
                icon: <MoveIcon />,
                titleKey: 'file.pivot.document.mutate.move.button',
                disabled: props.selectedDriveItems.length === 0,
                disabledReasonKey: 'file.pivot.document.disabled.select_items',
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => setOpenMutateDocuments({ action: MutateDocumentAction.Move, items: props.selectedDriveItems }),
            },
            {
                value: 'download',
                icon: <DownloadIcon />,
                titleKey: 'file.pivot.document.download.button',
                disabled: props.selectedDriveItems.length === 0,
                disabledReasonKey: 'file.pivot.document.disabled.select_items',
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => {
                    // A single file downloads directly; folders or multiple items become a zip
                    // via the existing zip generation flow.
                    if (singleSelectedFile) {
                        getDriveItemDownloadUrl(singleSelectedFile.id, axios).then(triggerBrowserDownload)
                        return
                    }
                    const payload = buildZipParams(props.selectedDriveItems, props.file.documentRootId, props.file.reference)
                    submitZipRequest(payload, payload.fileName, {
                        [QueueStatus.Finished]: (e: QueueData<any, DocumentGenerationStatusData, DocumentGenerationContextData>) => {
                            triggerBrowserDownload(getDownloadUrl(e.status.driveItem))
                        },
                    })
                },
            },
            {
                value: 'delete',
                icon: <DeleteIcon />,
                titleKey: 'file.pivot.document.delete.button',
                disabled: props.selectedDriveItems.length === 0,
                disabledReasonKey: 'file.pivot.document.disabled.select_items',
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => setOpenDeleteDocuments(true),
            },
            {
                value: 'import',
                icon: <ImportIcon />,
                titleKey: 'file.pivot.document.import.button',
                filter: () => viewType === ViewType.TreeView && !isAppContext([AppContext.Compose]),
                onClick: () => dropzoneRef.current?.open(),
            },
            {
                value: 'divider2',
                divider: true,
            },
            {
                value: 'sort',
                icon: <SortIcon />,
                titleKey: 'sorting.label',
                childButtons: [
                    {
                        value: 'filter-a-z',
                        titleKey: 'sorting.az',
                        icon: <SortIcon />,
                        onClick: () => setSortOrder(DocumentSortOrder.NameAsc),
                    },
                    {
                        value: 'filter-z-a',
                        titleKey: 'sorting.za',
                        icon: <SortIcon />,
                        onClick: () => setSortOrder(DocumentSortOrder.NameDesc),
                    },
                    {
                        value: 'filter-old-new',
                        titleKey: 'sorting.dateasc',
                        icon: <SortIcon />,
                        onClick: () => setSortOrder(DocumentSortOrder.ModifiedAsc),
                    },
                    {
                        value: 'filter-new-old',
                        titleKey: 'sorting.datedesc',
                        icon: <SortIcon />,
                        onClick: () => setSortOrder(DocumentSortOrder.ModifiedDesc),
                    },
                ],
            },
            // Tree/list view switch (HAM-4057): menu with two buttons right of the sort button; the active view shows a filled icon in brand color.
            {
                value: 'view-type',
                icon: viewType === ViewType.TreeView ? <ListBarTreeOffsetRegular /> : <ListBarRegular />,
                titleKey: 'file.pivot.document.view.tooltip',
                childButtons: [
                    {
                        value: 'view-tree',
                        icon: viewType === ViewType.TreeView ? <ListBarTreeOffsetFilled /> : <ListBarTreeOffsetRegular />,
                        titleKey: 'file.pivot.document.view.treeview',
                        onClick: () => switchView(ViewType.TreeView),
                    },
                    {
                        value: 'view-list',
                        icon: viewType === ViewType.ListView ? <ListBarFilled /> : <ListBarRegular />,
                        titleKey: 'file.pivot.document.view.listview',
                        onClick: () => switchView(ViewType.ListView),
                    },
                ],
            },
            {
                // Plain button entry (no render): this way it also works as a MenuItem when it
                // lands in the overflow menu — the popover itself is controlled and lives outside
                // the toolbar, anchored to the toolbar row.
                value: 'selection-help',
                icon: <HelpIcon />,
                titleKey: 'file.pivot.document.help.button',
                onClick: () => setHelpOpen(open => !open),
            },
        ].filter(Boolean)
    }, [props.selectedDriveItems, setSortOrder, handleMoveItems, axios, translate, viewType, switchView])

    if (!loadedSortOrder) {
        return <Loading />
    }

    return (
        <>
            <Card className={appStyles.sticky}>
                <HammockFormDialog<Partial<OfficeDocumentDialogBodyValues>, OfficeDocumentDialogBodyProps>
                    open={openCreateStandardDocument}
                    setOpen={setOpenCreateStandardDocument}
                    dialogFormProps={() => ({
                        initialValues: {
                            fileId: props.fileId,
                            folderId: props.selectedDriveItems.filter(item => item.itemType === 'branch')?.[0]?.id,
                        },
                        onSubmit: () => {},
                        validationSchema: getOfficeDocumentDialogBodySchema(translate),
                    })}
                    dialogFormBody={OfficeDocumentDialogBody}
                    dialogFormBodyProps={() => ({
                        context: DocumentContext.Document,
                        file: props.file,
                        documentRootId: props.file.documentRootId,
                        folders: allDriveItems,
                        parentId: props.selectedDriveItems.filter(item => item.itemType === 'branch')?.[0]?.id,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                        },
                    })}
                />
                <HammockFormDialog<CreateFolderDialogBodyValues, CreateFolderDialogBodyProps>
                    open={openCreateRootFolder}
                    setOpen={setOpenCreateRootFolder}
                    dialogFormProps={() => ({
                        initialValues: {
                            folderId: undefined,
                            name: undefined,
                        },
                        onSubmit: () => {},
                        validationSchema: getCreateRootFolderDialogBodySchema(translate),
                    })}
                    dialogFormBody={CreateFolderDialogBody}
                    dialogFormBodyProps={() => ({
                        isRoot: true,
                        folders: allDriveItems,
                        parentId: props.file.documentRootId,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            // A new folder changed the tree — drop the party picker's cached copy.
                            invalidateFolderTree()
                        },
                    })}
                />
                <HammockFormDialog<CreateFolderDialogBodyValues, CreateFolderDialogBodyProps>
                    open={openCreateSubFolder}
                    setOpen={setOpenCreateSubFolder}
                    dialogFormProps={() => ({
                        initialValues: {
                            folderId: props.selectedDriveItems.filter(item => item.itemType === 'branch')?.[0]?.id,
                            name: undefined,
                        },
                        onSubmit: () => {},
                        validationSchema: getCreateSubFolderDialogBodySchema(translate),
                    })}
                    dialogFormBody={CreateFolderDialogBody}
                    dialogFormBodyProps={() => ({
                        isRoot: false,
                        folders: allDriveItems,
                        parentId: undefined,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                            // A new subfolder changed the tree — drop the party picker's cached copy.
                            invalidateFolderTree()
                        },
                    })}
                />
                <HammockFormDialog<Partial<MutateDocumentDialogBodyValues>, MutateDocumentDialogBodyProps>
                    open={openMutateDocuments?.items?.length > 0}
                    setOpen={(open: boolean) =>
                        setOpenMutateDocuments(open ? openMutateDocuments : { action: MutateDocumentAction.Rename, items: [] })
                    }
                    dialogFormProps={() => ({
                        initialValues: {
                            name: openMutateDocuments.items.length === 1 ? openMutateDocuments?.items?.[0]?.name : undefined,
                            fileId: props.fileId,
                            folderId: openMutateDocuments?.items?.[0]?.parent?.id,
                        },
                        onSubmit: () => {},
                        validationSchema: getMutateDocumentDialogBodySchema(translate, openMutateDocuments?.items),
                    })}
                    dialogFormBody={MutateDocumentDialogBody}
                    dialogFormBodyProps={() => ({
                        file: props.file,
                        folders: allDriveItems,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                            // Move/rename/delete may have touched a folder — drop the party picker's cache.
                            invalidateFolderTree()
                        },
                        ...openMutateDocuments,
                    })}
                />
                <HammockDialog<DeleteDocumentDialogBodyProps>
                    open={openDeleteDocuments}
                    setOpen={setOpenDeleteDocuments}
                    dialogBody={DeleteDocumentDialogBody}
                    dialogBodyProps={() => ({
                        items: props.selectedDriveItems,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                            // Deletion may have removed a folder — drop the party picker's cache.
                            invalidateFolderTree()
                        },
                    })}
                />
                <HammockDialog<PreviewDocumentDialogBodyProps>
                    open={openPreviewDocument !== undefined}
                    setOpen={(open: boolean) => setOpenPreviewDocument(open ? openPreviewDocument : undefined)}
                    dialogBody={PreviewDocumentDialogBody}
                    surfaceProps={{
                        className: dialogStyles.dialogHeight,
                    }}
                    dialogBodyProps={() => ({
                        driveItem: openPreviewDocument,
                        callback: () => reloadDriveItemsFunc.current?.(),
                        hasPrevious: () => openPreviewDocument && documentDriveItems?.findIndex(value => value.id === openPreviewDocument.id) > 0,
                        hasNext: () =>
                            openPreviewDocument &&
                            documentDriveItems?.length > documentDriveItems?.findIndex(value => value.id === openPreviewDocument.id) + 1,
                        previous: () => {
                            const curIndex = documentDriveItems?.findIndex(value => value.id === openPreviewDocument.id)
                            if (curIndex > 0) {
                                setOpenPreviewDocument(documentDriveItems?.[curIndex - 1])
                            }
                        },
                        next: () => {
                            const curIndex = documentDriveItems?.findIndex(value => value.id === openPreviewDocument.id)
                            if (documentDriveItems?.length > curIndex) {
                                setOpenPreviewDocument(documentDriveItems?.[curIndex + 1])
                            }
                        },
                    })}
                />
                <HammockDialog<ComposeEmailDialogBodyProps>
                    open={openComposeEmail}
                    setOpen={setOpenComposeEmail}
                    dialogBody={ComposeEmailDialogBody}
                    dialogBodyProps={() => props}
                />
                <HammockFormDialog<Partial<ImportDocumentDialogBodyValues>, ImportDocumentDialogBodyProps>
                    open={openImportDocuments?.items?.length > 0}
                    setOpen={(open: boolean) => setOpenImportDocuments(open ? openImportDocuments : { items: [] })}
                    dialogFormProps={() => ({
                        initialValues: {
                            folderId: openImportDocuments?.folderId ?? props.selectedDriveItems[0]?.id,
                        },
                        onSubmit: () => {},
                        validationSchema: getImportDocumentDialogBodySchema(translate),
                    })}
                    dialogFormBody={ImportDocumentDialogBody}
                    dialogFormBodyProps={() => ({
                        file: props.file,
                        folders: allDriveItems,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                        },
                        ...openImportDocuments,
                    })}
                />

                <div className={overviewStyles.toolbarRow} ref={toolbarRowRef}>
                    <OverflowToolbarPanel
                        toolbarProps={toolbarProps}
                        getToolbarButtonProps={(button?: HammockToolbarButtonData) => ({
                            appearance: 'subtle',
                            // disabledFocusable instead of disabled: a natively disabled button gets
                            // no hover events, so the why-disabled tooltip would never show.
                            disabledFocusable: isLoading || button?.disabled,
                        })}
                        getMenuItemProps={(button?: HammockToolbarButtonData) => ({
                            disabled: isLoading || button?.disabled,
                        })}
                        containerClassName={pageStyles.toolbar}
                        buttons={buttons}
                        filter={buttons => buttons?.filter(button => (button.filter ? button.filter(button) : true))}
                        overflowButtonLabelKey={'base.item.label'}
                    />
                    {props.selectedDriveItems.length > 0 && (
                        <div className={overviewStyles.selectionPill} role="status" aria-live="polite">
                            <Button
                                appearance="subtle"
                                size="small"
                                icon={<DismissRegular />}
                                iconPosition="after"
                                onClick={clearSelection}
                                aria-label={translate('file.pivot.document.selection.clear')}
                            >
                                {viewportNarrow ? (
                                    <Badge appearance="filled" color="brand" size="small">
                                        {props.selectedDriveItems.length}
                                    </Badge>
                                ) : (
                                    translate('file.pivot.document.selection.count', { count: props.selectedDriveItems.length })
                                )}
                            </Button>
                        </div>
                    )}
                </div>
                <Popover
                    withArrow
                    open={helpOpen}
                    onOpenChange={(_, data) => setHelpOpen(data.open)}
                    positioning={{ target: toolbarRowRef.current, position: 'below', align: 'end' }}
                >
                    <PopoverSurface>
                        <div className={overviewStyles.helpPopover}>
                            <b>{translate('file.pivot.document.help.title')}</b>
                            <ul className={overviewStyles.helpList}>
                                <li>{translate('file.pivot.document.help.click')}</li>
                                <li>{translate('file.pivot.document.help.shift')}</li>
                                <li>{translate('file.pivot.document.help.double')}</li>
                                <li>{translate('file.pivot.document.help.folder')}</li>
                                <li>{translate('file.pivot.document.help.keyboard')}</li>
                                <li>{translate('file.pivot.document.help.drag')}</li>
                            </ul>
                        </div>
                    </PopoverSurface>
                </Popover>
                {errors?.map((error, index) => (
                    <MessageBar intent={'error'} key={index}>
                        <MessageBarBody>{error.message}</MessageBarBody>
                        <MessageBarActions
                            containerAction={
                                <Button
                                    appearance="transparent"
                                    aria-label="Dismiss"
                                    icon={<DismissRegular />}
                                    onClick={() => setErrors([...errors.filter(item => item.message !== error.message)])}
                                />
                            }
                        ></MessageBarActions>
                    </MessageBar>
                ))}
            </Card>
            <Card>
                <Dropzone
                    disabled={viewType === ViewType.ListView || allDriveItems?.filter(item => item.itemType === 'branch').length === 0}
                    dropzoneRef={dropzoneRef}
                    onDropAccepted={(files: File[], event) => {
                        // Files dropped on a folder row preset that folder in the import dialog;
                        // anywhere else keeps the generic import flow.
                        const target = event?.target as HTMLElement | undefined
                        const folderRow =
                            typeof target?.closest === 'function' ? target.closest<HTMLElement>('[data-node-id][data-node-type="folder"]') : null
                        setOpenImportDocuments({ items: files, folderId: folderRow?.dataset.nodeId })
                    }}
                >
                    {viewType === ViewType.ListView ? (
                        <DocumentFlatList
                            size={smallMenu ? 'small' : 'medium'}
                            rootId={props.file.documentRootId}
                            referenceNumber={props.file.reference}
                            sortOrder={sortOrder}
                            selectedOptions={props.selectedDriveItemIds}
                            onOptionsSelect={(ids, items) => {
                                props.setSelectedDriveItemIds(ids)
                                props.setSelectedDriveItems(items)
                            }}
                            setIsLoading={setIsLoading}
                            onDriveItemsLoaded={(allItems, displayedItems) => {
                                setAllDriveItems(allItems)
                                setFilteredDriveItems(displayedItems)
                            }}
                            setReloadDriveItems={registerReloadDriveItems}
                            onPreviewClick={driveItem => setOpenPreviewDocument(driveItem)}
                            cachedData={flatListCacheRef.current}
                            onDataFetched={cache => (flatListCacheRef.current = cache)}
                        />
                    ) : (
                        <DocumentFlatTree
                            size={smallMenu ? 'small' : 'medium'}
                            rootId={props.file.documentRootId}
                            referenceNumber={props.file.reference}
                            sortOrder={sortOrder}
                            onMoveItems={handleMoveItems}
                            selectedOptions={props.selectedDriveItemIds}
                            onOptionsSelect={(ids, items) => {
                                props.setSelectedDriveItemIds(ids)
                                props.setSelectedDriveItems(items)
                            }}
                            setIsLoading={setIsLoading}
                            onDriveItemsLoaded={(allItems, filteredItems) => {
                                setAllDriveItems(allItems)
                                setFilteredDriveItems(filteredItems)
                            }}
                            setUpdateDriveItems={setter => (updateDriveItemsFunc.current = setter)}
                            setReloadDriveItems={registerReloadDriveItems}
                            treeItemContent={(flatTree, props, flatTreeItem, isContentLoading, fileCount) => (
                                <SimpleDocumentFlatTreeItem
                                    key={flatTreeItem.value}
                                    {...flatTreeItem.getTreeItemProps()}
                                    {...props}
                                    item={flatTreeItem.getTreeItemProps()}
                                    flatTreeHook={flatTree}
                                    itemType={flatTreeItem.itemType}
                                    size={props.size}
                                    isContentLoading={isContentLoading}
                                    fileCount={fileCount}
                                    onPreviewClick={driveItem => setOpenPreviewDocument(driveItem)}
                                />
                            )}
                        />
                    )}
                </Dropzone>
            </Card>
        </>
    )
}
